/**
 * THE ENTITY CHAT'S ONE TOOL — the shape the model fills, Spotlight's parse lane as the pattern
 * (`ui-editor/spotlight/parse/parse-prompt.ts`): every optional field is `[T, 'null']`, every enum lists
 * `null` explicitly when the field may be empty, every object is closed (`additionalProperties: false`).
 * DeepSeek emits `null` for fields it has nothing for; a schema that forbids it gets rejected output.
 * The validator (`entity-validate.ts`) checks the same lists — both read the arrays exported here.
 */
import { OFFERED_FIELD_TYPES } from '../rules/entity-form-rules';

// Was (2026-10-03, plan 4b): export const PARSE_KINDS = ['edits', 'answer', 'ask', 'decline'] as const;
export const PARSE_KINDS = ['edits', 'answer', 'ask', 'decline', 'action'] as const;
export const ACTION_OPS = ['import', 'sync', 'grant', 'revoke', 'remove_role'] as const;
export const PERMISSION_WORDS = ['create', 'read', 'update', 'delete'] as const;
// Was (2026-10-03, plan 4a): export const EDIT_OPS = ['create_entity', 'add_field', 'edit_field'] as const;
export const EDIT_OPS = ['create_entity', 'add_field', 'edit_field', 'add_row', 'edit_row', 'remove_row'] as const;
export const DECLINE_REASONS = ['pages', 'data', 'delete_field', 'delete_entity', 'create_role', 'other'] as const;
export const DEFAULT_MODES = ['now', 'fixed', 'none'] as const;

const text = (description: string) => ({ type: ['string', 'null'], description });
const flag = (description: string) => ({ type: ['boolean', 'null'], description });
const words = (description: string) => ({ type: ['array', 'null'], items: { type: 'string' }, description });

const EDIT = {
  type: 'object',
  properties: {
    op: { type: 'string', enum: [...EDIT_OPS], description: 'What this edit does.' },
    entity: text('The entity, by name. Null = the OPEN ENTITY.'),
    local: flag('create_entity only: true for a local entity (in the app only, no table).'),
    path: words('The names of the lists of items the field sits in, outermost first. Null or [] = top level.'),
    field: text('add_field: the new field\'s name. edit_field: the field to change, by its current name.'),
    newName: text('edit_field: the new name, when renaming.'),
    type: { type: ['string', 'null'], enum: [...OFFERED_FIELD_TYPES, null], description: 'The field\'s type.' },
    reference: text('For a link (ENTITY_REFERENCE): the linked entity\'s name.'),
    extendedAttributes: flag('For a list of items: true only if the builder asks for free-form extra attributes.'),
    defaultValue: { type: ['string', 'number', 'boolean', 'null'], description: 'The default, only when the sentence gives one.' },
    defaultMode: { type: ['string', 'null'], enum: [...DEFAULT_MODES, null], description: 'Date and time fields only.' },
    description: text('What the field is for, only when the sentence says so.'),
    // Was (2026-10-03, review round 1 — open dictionary had no precedent; DeepSeek rejects looser shapes):
    // values: {
    //   type: ['object', 'null'],
    //   additionalProperties: { type: ['string', 'number', 'boolean', 'null'] },
    //   description: 'add_row / edit_row: the cells, by field name.',
    // },
    values: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        properties: { field: { type: 'string' }, value: { type: ['string', 'number', 'boolean', 'null'] } },
        required: ['field', 'value'],
        additionalProperties: false,
      },
      description: 'add_row / edit_row: the cells, one {field, value} per cell.',
    },
    row: {
      type: ['object', 'null'],
      properties: { field: { type: 'string' }, value: { type: ['string', 'number', 'boolean'] } },
      required: ['field', 'value'],
      additionalProperties: false,
      description: 'edit_row / remove_row: pick the row whose field holds this value.',
    },
    rowNumber: { type: ['integer', 'null'], description: 'edit_row / remove_row: the row by position, 1 = the first.' },
  },
  required: ['op'],
  additionalProperties: false,
};

const ACTION = {
  type: ['object', 'null'],
  properties: {
    op: { type: 'string', enum: [...ACTION_OPS], description: 'The page action.' },
    entity: text('import: the standard entity\'s name as listed. grant / revoke / remove_role: the entity (null = the OPEN ENTITY).'),
    role: text('grant / revoke / remove_role: the role\'s name, from ROLES.'),
    permissions: {
      type: ['array', 'null'],
      items: { type: 'string', enum: [...PERMISSION_WORDS] },
      description: 'grant / revoke: which permissions.',
    },
  },
  required: ['op'],
  additionalProperties: false,
  description: 'kind = action: an import, a Sync or a permission change.',
};

export const ENTITY_TOOL = {
  type: 'function' as const,
  function: {
    name: 'entity_request',
    description: 'Record what the builder asked for about entities and their fields. Leave a field null when the sentence does not say it.',
    parameters: {
      type: 'object',
      properties: {
        // Was (2026-10-03, plan 4b): kind: { type: 'string', enum: [...PARSE_KINDS], description: 'edits, answer, ask or decline.' },
        kind: { type: 'string', enum: [...PARSE_KINDS], description: 'edits, answer, ask, decline or action.' },
        edits: { type: ['array', 'null'], items: EDIT, description: 'kind = edits: one object per change, in order.' },
        answer: text('kind = answer: one or two plain sentences, only from ENTITIES.'),
        question: text('kind = ask: one short question.'),
        options: words('kind = ask: up to 6 short choices.'),
        decline: { type: ['string', 'null'], enum: [...DECLINE_REASONS, null], description: 'kind = decline: why.' },
        gaps: words('Short phrases naming what was asked for but cannot be done here (option lists, required, unique, calculated).'),
        action: ACTION,
      },
      required: ['kind'],
      additionalProperties: false,
    } as Record<string, unknown>,
  },
};
